import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";

interface SearchResult {
  id: string;
  title: string;
  subtitle: string | null;
  content: string | null;
  summary: string | null;
  image_url: string | null;
  category: string | null;
  categories?: string[] | null;
  tags: string[] | null;
  source: string | null;
  source_url: string | null;
  author: string | null;
  slug: string | null;
  published_at: string | null;
  views: number | null;
  type: "published" | "imported";
  created_at: string;
  status?: string | null;
}

interface UseSearchResult {
  results: SearchResult[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  activeFilter: string;
  setActiveFilter: (filter: string) => void;
  activeType: string;
  setActiveType: (type: string) => void;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  itemsPerPage: number;
  paginatedResults: SearchResult[];
  totalPages: number;
  performSearch: (term: string) => void;
}

const itemsPerPage = 10;

export function useSearch(): UseSearchResult {
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState("Todas");
  const [activeType, setActiveType] = useState("Todas");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const performSearch = useCallback(
    async (term: string) => {
      if (!term.trim()) {
        setResults([]);
        setTotalCount(0);
        setCurrentPage(1);
        return;
      }

      setLoading(true);
      setError(null);
      setCurrentPage(1);

      const searchPattern = `%${term.trim()}%`;

      try {
        const allResults: SearchResult[] = [];

        // Search in published news (news table)
        const { data: newsData, error: newsError } = await supabase
          .from("news")
          .select("id, title, subtitle, content, image_url, category, categories, tags, source, source_url, author, slug, published_at, views, created_at, status")
          .or(
            `title.ilike.${searchPattern},content.ilike.${searchPattern},subtitle.ilike.${searchPattern},category.ilike.${searchPattern},source.ilike.${searchPattern}`
          )
          .eq("status", "published")
          .order("published_at", { ascending: false });

        if (newsError) {
          setError(newsError.message);
          setLoading(false);
          return;
        }

        if (newsData) {
          newsData.forEach((item) => {
            allResults.push({
              ...item,
              summary: null,
              type: "published",
            });
          });
        }

        // Search in imported RSS feeds (rss_feeds table)
        const { data: rssData, error: rssError } = await supabase
          .from("rss_feeds")
          .select("id, title, subtitle, content, summary, image_url, category, tags, source_name, original_url, author, published_date, created_at, status")
          .or(
            `title.ilike.${searchPattern},content.ilike.${searchPattern},summary.ilike.${searchPattern},category.ilike.${searchPattern},source_name.ilike.${searchPattern}`
          )
          .eq("status", "published")
          .order("published_date", { ascending: false });

        if (rssError) {
          setError(rssError.message);
          setLoading(false);
          return;
        }

        if (rssData) {
          rssData.forEach((item) => {
            allResults.push({
              id: item.id,
              title: item.title,
              subtitle: item.subtitle || null,
              content: item.content || null,
              summary: item.summary || null,
              image_url: item.image_url || null,
              category: item.category || null,
              tags: item.tags || null,
              source: item.source_name || null,
              source_url: item.original_url || null,
              author: item.author || null,
              slug: null,
              published_at: item.published_date || null,
              views: null,
              created_at: item.created_at,
              status: item.status,
              type: "imported",
            });
          });
        }

        // Sort by date (most recent first)
        allResults.sort((a, b) => {
          const dateA = new Date(a.published_at || a.created_at).getTime();
          const dateB = new Date(b.published_at || b.created_at).getTime();
          return dateB - dateA;
        });

        setResults(allResults);
        setTotalCount(allResults.length);
      } catch (err) {
        setError("Erro ao buscar. Tente novamente.");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Filter results
  const filteredResults = results.filter((item) => {
    const typeMatch =
      activeType === "Todas" ||
      (activeType === "Publicadas" && item.type === "published") ||
      (activeType === "Importadas" && item.type === "imported");

    const categoryMatch =
      activeFilter === "Todas" ||
      item.category === activeFilter ||
      Boolean(item.categories?.includes(activeFilter));

    return typeMatch && categoryMatch;
  });

  const totalPages = Math.ceil(filteredResults.length / itemsPerPage);
  const paginatedResults = filteredResults.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return {
    results: filteredResults,
    loading,
    error,
    totalCount: filteredResults.length,
    searchTerm,
    setSearchTerm,
    activeFilter,
    setActiveFilter,
    activeType,
    setActiveType,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    paginatedResults,
    totalPages,
    performSearch,
  };
}
